import {
  computed,
  effect,
  inject,
  Injectable,
  isDevMode,
  signal,
} from "@angular/core";
import { Router } from "@angular/router";
import { JoyrideService } from "ngx-joyride";
import { NGXLogger } from "ngx-logger";
import { BehaviorSubject } from "rxjs";
import { UtilsService } from "src/app/shared/services/utils.service";
import { AccessRequest } from "../models/access-request.model";
import { DashboardConfig, OnBoardingSection, User } from "../models/user.model";
import { AccessRequestService } from "../services/accounts/access-request.service";
import { UserService } from "../services/user.service";
import { AppStore } from "./app.store";
import { AuthStore } from "./auth.store";

const LOG_TAG = "[USER-STORE] ";
@Injectable({
  providedIn: "root",
})
export class UserStore {
  private logger: NGXLogger = inject(NGXLogger);
  private userService: UserService = inject(UserService);
  private utilsService: UtilsService = inject(UtilsService);
  private accessRequestService: AccessRequestService =
    inject(AccessRequestService);
  private authStore: AuthStore = inject(AuthStore);
  private appStore: AppStore = inject(AppStore);
  private joyride: JoyrideService = inject(JoyrideService);
  private router: Router = inject(Router);

  private readonly doing = signal(false);
  readonly doing$ = this.doing.asReadonly();

  private readonly error = signal<string | null>(null);
  readonly error$ = this.error.asReadonly();

  // ---------------------
  // --- auth
  // ---------------------

  private readonly authLoading = signal<boolean>(false);
  readonly authLoading$ = this.authLoading.asReadonly();

  private readonly authError = signal<string | null>(null);
  readonly authError$ = this.authError.asReadonly();

  // ---------------------
  // --- user
  // ---------------------

  private userLastFetch: Date;
  private userRefreshDelay: number = 3600; // 1 hour

  private readonly loading = signal(false);
  readonly loading$ = this.loading.asReadonly();

  private readonly user = signal<User>(null);
  readonly user$ = computed(() => {
    const user = this.user();

    if (user === null) return null;

    return user.clone();
  });

  // Getter observables from signals
  userObs = new BehaviorSubject<User>(this.user$());
  private firstUserLoaded: boolean = false;

  readonly userTimezone$ = computed(() =>
    this.user$() && this.user$().timezone ? this.user$().timezone : null
  );
  readonly userLocale$ = computed(() => this.user$() && this.user$().locale);
  readonly userEmail$ = computed(() => this.user$() && this.user$().email);
  readonly isAdmin$ = computed(() => this.user$() && this.user$().isAdmin);
  readonly canViewPlaces$ = computed(() => {
    const specialCases = this.appStore.specialCases$();
    const appCanViewPlaces = this.appStore.canViewPlaces$();
    const user = this.user$();

    let result;
    if (specialCases === true) {
      result = appCanViewPlaces;
    } else {
      result =
        user != null &&
        user.features != null &&
        user.features.has("web_view_places");
    }
    return result;
  });
  readonly canViewMeters$ = computed(
    () =>
      this.user() != null &&
      this.user().features != null &&
      this.user().features.has("web_view_meters")
  );
  readonly canViewMeterUsage$ = computed(
    () =>
      this.user() != null &&
      this.user().features != null &&
      this.user().features.has("meter_labelling")
  );
  readonly canViewSh$ = computed(
    () =>
      this.user() != null &&
      this.user().features != null &&
      this.user().features.has("web_view_sh")
  );
  readonly canViewAlerts$ = computed(
    () =>
      this.user() != null &&
      this.user().features != null &&
      this.user().features.has("web_view_alerts")
  );
  readonly canAddSh$ = computed(
    () =>
      this.user() != null &&
      this.user().features != null &&
      this.user().features.has("web_add_sh_by_serial")
  );
  readonly canChangeThreshold$ = computed(
    () =>
      this.user() != null &&
      this.user().features != null &&
      this.user().features.has("web_change_threshold")
  );
  readonly canManageChildAccounts$ = computed(
    () =>
      this.user() != null &&
      this.user().features != null &&
      this.user().features.has("web_request_child_account")
  );
  readonly canCreateSmartAlerts$ = computed(
    () =>
      this.user() != null &&
      this.user().features != null &&
      this.user().features.has("smart_alerts")
  );
  readonly canNotifySMS$ = computed(
    () =>
      this.user() != null &&
      this.user().features != null &&
      this.user().features.has("sms_notifications")
  );
  readonly canViewMeterHourlySignal$ = computed(
    () =>
      this.user() != null &&
      this.user().features != null &&
      this.user().features.has("meter_hourly_signal")
  );
  readonly canViewGateways$ = computed(
    () =>
      this.user() != null &&
      this.user().features != null &&
      this.user().features.has("web_view_gateways")
  );
  readonly hasCustomPages$ = computed(
    () =>
      this.user() != null &&
      this.user().custom_pages != null &&
      this.user().custom_pages.length > 0
  );
  readonly useGallons$ = computed(
    () =>
      this.user() != null &&
      this.user().water_unit != null &&
      this.user().water_unit !== "L"
  );
  readonly useLiters$ = computed(() => !this.useGallons$());

  // Fetch user data from an API
  fetchUser(): void {
    if (this.loading()) return;

    // check if refresh necessary
    if (
      !this.userLastFetch ||
      this.utilsService.calculateElapsedSeconds(this.userLastFetch) >
        this.userRefreshDelay
    ) {
      this.loading.set(true);
      this.error.set(null);
      console.log(LOG_TAG + "MIGRATION / fetchUser...");

      this.userService.getUserDataForStore().subscribe({
        next: (user: User) => {
          if (!user.locale) {
            // init locale from browser and update user data
            let browserLocale =
              navigator.language || (navigator as any).userLanguage;
            if (browserLocale == "fr") {
              this.logger.debug("browser locale seems to be fr");
              user.locale = "fr";
            } else {
              this.logger.debug("brower local is not fr, set it to en");
              user.locale = "en";
            }
            if (isDevMode()) {
              this.user.set(this._fixUserData(user));
              console.log(LOG_TAG + "Skip update user locale for DEV");
            } else {
              this.logger.info(
                `update user locale from brwoser : ${user.locale}`
              );
              this.updateUser(user);
            }
          } else {
            this.user.set(this._fixUserData(user)); // Update the state with the fetched data
          }

          this.loading.set(false);
          this.userLastFetch = new Date();
          this.authStore.updateLogged(true);
        },
        error: (err) => {
          console.error(LOG_TAG + "failed to fetch user data : ", err);
          this.error.set(err.message || "An error occurred.");
          this.loading.set(false);
        },
      });
    }
  }

  private readonly updating = signal(false);
  readonly updating$ = this.updating.asReadonly();

  updateUser(newUser: User): void {
    this.updating.set(true);
    this.userService.saveUserDataForStore(newUser).subscribe({
      next: (user: User) => {
        const fixedUser = this._fixUserData(user);
        console.log(LOG_TAG + "UPDATED USER", user, fixedUser);
        this.user.set(fixedUser);
        this.updating.set(false);
      },
      error: (err) => {
        this.error.set(err.message || "An error occurred.");
        this.updating.set(false);
      },
    });
  }

  setUser(newUser: User) {
    this.user.set(newUser);
  }

  // ---------------------
  // --- access requests
  // ---------------------

  private readonly accessRequestsLastFetch = signal<Date>(null);
  private accessRequestsRefreshDelay: number = 3600; // 1 hour

  private readonly loadingAccessRequests = signal(false);
  readonly loadingAccessRequests$ = computed(() => {
    return (
      this.accessRequestsLastFetch() === null ||
      this.loadingAccessRequests() === true
    );
  });

  private readonly accessRequests = signal<AccessRequest[]>([]);
  readonly accessRequests$ = this.accessRequests.asReadonly();

  private readonly accessRequestsDialogShowed = signal<boolean>(false);
  readonly accessRequestsDialogShowed$ =
    this.accessRequestsDialogShowed.asReadonly();

  // Fetch user access requests from an API
  fetchAccessRequests(forced: boolean = false): void {
    if (this.loadingAccessRequests()) return;

    // check if refresh necessary
    if (
      forced === true ||
      !this.accessRequestsLastFetch() ||
      this.utilsService.calculateElapsedSeconds(
        this.accessRequestsLastFetch()
      ) > this.accessRequestsRefreshDelay
    ) {
      this.loadingAccessRequests.set(true);
      this.error.set(null);
      console.log(LOG_TAG + "MIGRATION / fetchUserAccessRequests...");

      this.accessRequestService.getAccessRequestsForStore().subscribe({
        next: (requests: AccessRequest[]) => {
          this.accessRequests.set(requests);
          console.log(LOG_TAG + "new access requests", requests);
          this.loadingAccessRequests.set(false);
          this.accessRequestsLastFetch.set(new Date());
        },
        error: (err) => {
          console.error(
            LOG_TAG + "failed to fetch user access requests : ",
            err
          );
          this.error.set(err.message || "An error occurred.");
          this.loadingAccessRequests.set(false);
        },
      });
    }
  }

  updateAccessRequestsDialogShowed(showed): void {
    if (this.accessRequestsDialogShowed() !== showed) {
      this.accessRequestsDialogShowed.set(showed);
    }
  }

  // ---------------------
  // --- access requests actions
  // ---------------------

  private readonly acceptingAccessRequest = signal<boolean>(false);
  readonly acceptingAccessRequest$ = this.acceptingAccessRequest.asReadonly();
  private readonly acceptResult = signal<boolean | string | null>(null);
  readonly acceptResult$ = this.acceptResult.asReadonly();

  acceptAccessRequest(userId: number): void {
    if (this.acceptingAccessRequest()) return;

    this.acceptResult.set(null);
    this.acceptingAccessRequest.set(true);

    const tourMode = this.appStore.tourMode$();

    if (!tourMode) {
      this.accessRequestService.acceptRequestForStore(userId).subscribe({
        next: (accessRequest: AccessRequest) => {
          this.acceptingAccessRequest.set(false);
          this.acceptResult.set(true);
          console.log(LOG_TAG + "access request accepted", userId);
        },
        error: (err) => {
          console.error(LOG_TAG + "failed to accept access request : ", err);
          this.acceptResult.set(err.error_code || "unknown");
          this.acceptingAccessRequest.set(false);
        },
      });
    }
  }

  declareAcceptSuccess() {
    this.acceptResult.set(null);

    const tourMode = this.appStore.tourMode$();
    if (tourMode === false) {
      // refresh items
      this.fetchAccessRequests(true);
    }
  }

  resetAcceptResult() {
    this.acceptResult.set(null);
  }

  private readonly denyingAccessRequest = signal<boolean>(false);
  readonly denyingAccessRequest$ = this.denyingAccessRequest.asReadonly();
  private readonly denyResult = signal<boolean | string | null>(null);
  readonly denyResult$ = this.denyResult.asReadonly();

  denyAccessRequest(userId: number): void {
    if (this.denyingAccessRequest()) return;

    this.denyResult.set(null);
    this.denyingAccessRequest.set(true);

    const tourMode = this.appStore.tourMode$();

    if (!tourMode) {
      this.accessRequestService.denyRequestForStore(userId).subscribe({
        next: (result) => {
          this.denyingAccessRequest.set(false);
          this.denyResult.set(true);
          console.log(LOG_TAG + "access request denied", userId);
        },
        error: (err) => {
          console.error(LOG_TAG + "failed to deny access request : ", err);
          this.denyResult.set(err.error_code || "unknown");
          this.denyingAccessRequest.set(false);
        },
      });
    }
  }

  declareDenySuccess() {
    this.denyResult.set(null);

    const tourMode = this.appStore.tourMode$();
    if (tourMode === false) {
      // refresh items
      this.fetchAccessRequests(true);
    }
  }

  resetDenyResult() {
    this.denyResult.set(null);
  }

  // ---------------------
  // --- touring
  // ---------------------

  private touring: boolean = false;

  public startTour(
    section: OnBoardingSection,
    steps: string[],
    urlOnFinish: string
  ) {
    const tourForced = this.appStore.tourForced$();

    if (this.touring === true) return;

    console.log(
      LOG_TAG + "start tour ?",
      section,
      steps,
      urlOnFinish,
      tourForced
    );

    if (!this._needTour(section) || !Array.isArray(steps) || steps.length === 0)
      return;

    console.log(LOG_TAG + "starting tour", section);

    this.touring = true;
    this.appStore.updateTourMode(true);
    this.joyride
      .startTour({
        waitingTime: 300,
        steps: steps,
        customTexts: {
          next: $localize`next`,
          prev: $localize`prev`,
          done: $localize`done`,
        },
      })
      .subscribe({
        next: async (step) => {
          // console.log(step);
          // if (step.name == 'selectDate') {
          //   this._dateSvc.openPanel();
          // }
        },
        complete: async () => {
          this.logger.info(LOG_TAG, `tour "${section}" finished`);
          this.appStore.updateTourMode(false);
          // console.log("END OF TOUR", "tourMode=" + this.appStore.tourMode$());
          this.touring = false;
          if (tourForced === false) {
            this._disableOnboarding(section);
            // console.log("END OF TOUR", "on boarding updating...");
          } else {
            this.appStore.resetTourForced();
          }
          // console.log("END OF TOUR", "no redirect");

          // setTimeout(async () => {
          if (this.router.url === urlOnFinish) {
            // Même URL → forcer le reload
            this.router
              .navigateByUrl("/", { skipLocationChange: true })
              .then(() => {
                this.router.navigate([urlOnFinish]);
              });
          } else {
            await this.router.navigate([urlOnFinish], {
              replaceUrl: true,
              queryParamsHandling: "merge",
            });
          }
          // }, 1000);
        },
      });
  }

  // ---------------------
  // --- LIFE CYCLE METHODS
  // ---------------------

  public constructor() {
    effect(
      () => {
        this.userObs.next(this.user$());
      },
      { allowSignalWrites: true }
    );
  }

  public clear() {
    this.userLastFetch = null;

    this.user.set(null);

    this.accessRequests.set([]);
    this.accessRequestsLastFetch.set(null);
    this.accessRequestsDialogShowed.set(false);

    this.acceptResult.set(null);
    this.acceptingAccessRequest.set(false);

    this.denyResult.set(null);
    this.denyingAccessRequest.set(false);

    this.loading.set(false);
    this.error.set(null);
  }

  // ---- PRIVATE METHODS -------

  private _disableOnboarding(section: OnBoardingSection) {
    const user = this.user$().clone();

    user.disableOnBoarding(section);
    this.user.set(user); // update offline

    this.updateUser(user); // update online
  }

  private _fixUserData(user: User): User {
    if (!user.dashboard)
      user.dashboard = new DashboardConfig(
        DashboardConfig.DEFAULT_METER_DASH,
        DashboardConfig.DEFAULT_SH_DASH
      );
    if (!user.dashboard.meter)
      user.dashboard.meter = DashboardConfig.DEFAULT_METER_DASH;
    if (!user.dashboard.showerhead)
      user.dashboard.showerhead = DashboardConfig.DEFAULT_SH_DASH;
    if (!user.water_unit) user.water_unit = "L";

    // if (!user.locale) {
    //   let browserLocale = navigator.language || (navigator as any).userLanguage;
    //   if (browserLocale == "fr") {
    //     user.locale = "fr";
    //   } else {
    //     user.locale = "en";
    //   }
    // }

    return user;
  }

  private _needTour(section: OnBoardingSection) {
    const isAdmin = this.authStore.adminBehalf;
    const isMobile = this.appStore.mobileMode$();
    const tourForced = this.appStore.tourForced$();
    const user = this.user();

    console.log(LOG_TAG + "needTour", isAdmin, isMobile, tourForced, user);

    if (isAdmin || isMobile || user === null) return false;

    return !tourForced ? user.onboarding.indexOf(section) >= 0 : true;
  }
}
